import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSwitch } from '../components/LanguageSwitch';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, Clock, Users, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sphere labels for display
const SPHERE_LABELS = {
  // France
  SERVICES_FINANCIERS: { fr: "Services Financiers", en: "Financial Services" },
  SERVICES_AUX_ENTREPRISES: { fr: "Services aux Entreprises", en: "Business Services" },
  IMMOBILIER_ET_BATIMENT: { fr: "Immobilier et Bâtiment", en: "Real Estate & Construction" },
  RH_ET_FORMATION: { fr: "RH et Formation", en: "HR & Training" },
  SERVICES_AUX_PERSONNES: { fr: "Services aux Personnes", en: "Personal Services" },
  PUBLICITE_ET_COMMUNICATION: { fr: "Publicité et Communication", en: "Advertising & Communication" },
  SANTE_BIEN_ETRE: { fr: "Santé et Bien-être", en: "Health & Wellness" },
  TELECOM_ET_INFORMATIQUE: { fr: "Télécom et Informatique", en: "Telecom & IT" },
  // UK
  FINANCE_AND_LEGAL: { fr: "Finance et Juridique", en: "Finance & Legal" },
  PROPERTY: { fr: "Immobilier", en: "Property" },
  BUSINESS_SUPPORT: { fr: "Support Business", en: "Business Support" },
  LIFESTYLE_SERVICES: { fr: "Services Lifestyle", en: "Lifestyle Services" },
};

export const Welcome = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const guestId = searchParams.get('guest_id');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [guestData, setGuestData] = useState(null);
  const [spheres, setSpheres] = useState([]);
  const [selectedSphere, setSelectedSphere] = useState(null);
  const [step, setStep] = useState('welcome'); // 'welcome' or 'sphere'

  useEffect(() => {
    const initGuest = async () => {
      if (!guestId) {
        setError('invalid_link');
        setLoading(false);
        return;
      }

      try {
        // Get guest info (transparent - no database writes)
        const infoResponse = await axios.get(`${API}/guest/${guestId}/info`);
        const info = infoResponse.data;
        
        setGuestData(info);

        // Store test info in sessionStorage
        sessionStorage.setItem('apollo_guest_info', JSON.stringify({
          guestId: guestId,
          guestName: info.guest_name,
          groupName: info.group_name,
          country: info.country
        }));

        // If test already completed, redirect
        if (info.existing_test_completed) {
          navigate('/complete');
          return;
        }

        // Get spheres based on country
        const spheresResponse = await axios.get(`${API}/spheres/${info.country}`);
        setSpheres(spheresResponse.data.spheres);

      } catch (err) {
        console.error('Error getting guest info:', err);
        setError('error');
      } finally {
        setLoading(false);
      }
    };

    initGuest();
  }, [guestId, navigate]);

  const handleContinueToSphere = () => {
    setStep('sphere');
  };

  const handleStart = async () => {
    if (!selectedSphere) return;

    try {
      // Now create/get the test session
      const response = await axios.post(`${API}/guest/${guestId}/start-test`);
      
      // Store test info in sessionStorage
      sessionStorage.setItem('apollo_test', JSON.stringify({
        testId: response.data.test_id,
        guestId: guestId,
        guestName: guestData.guest_name,
        groupName: guestData.group_name,
        sphere: selectedSphere,
        country: guestData.country
      }));

      navigate('/test');
    } catch (err) {
      console.error('Error starting test:', err);
      setError('error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto" />
          <p className="text-gray-400 mt-4 font-dm-sans">{t('welcome.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4">
        <Card className="bg-gray-800 border-gray-700 max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
            <h2 className="text-xl font-manrope font-bold text-white mb-2">
              {error === 'invalid_link' ? t('welcome.invalid_link') : t('welcome.error')}
            </h2>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-6 py-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <img src="/apollo-logo.png" alt="Apollo" className="h-10 w-auto" />
        </div>
        <LanguageSwitch />
      </header>

      {/* Main Content */}
      <main className="flex items-center justify-center min-h-screen px-4 pt-20 pb-8">
        {step === 'welcome' ? (
          <Card className="bg-gray-800/90 border-gray-700 max-w-lg w-full backdrop-blur-sm shadow-xl" data-testid="welcome-card">
            <CardContent className="pt-10 pb-10 px-8">
              {/* Welcome Header */}
              <div className="text-center mb-8">
                <p className="text-orange-500 font-manrope font-semibold text-sm uppercase tracking-wider mb-2">
                  {t('welcome.subtitle')}
                </p>
                <h1 className="text-3xl sm:text-4xl font-manrope font-bold text-white mb-3">
                  {t('welcome.greeting')}, <span className="text-orange-500">{guestData?.guest_name}</span>
                </h1>
              </div>

              {/* Description */}
              <p className="text-gray-300 text-center mb-8 font-dm-sans leading-relaxed">
                {t('welcome.description')}
              </p>

              {/* Info Cards */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700 text-center">
                  <Clock className="w-6 h-6 text-orange-500 mx-auto mb-2" />
                  <p className="text-xs text-gray-400 font-dm-sans">~10 min</p>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700 text-center">
                  <Users className="w-6 h-6 text-orange-500 mx-auto mb-2" />
                  <p className="text-xs text-gray-400 font-dm-sans">22 questions</p>
                </div>
              </div>

              {/* CTA Button */}
              <Button
                data-testid="continue-btn"
                onClick={handleContinueToSphere}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-6 rounded-md shadow-[0_0_15px_rgba(249,115,22,0.3)] transition-all hover:scale-[1.02] text-lg"
              >
                {t('welcome.continue')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-gray-800/90 border-gray-700 max-w-lg w-full backdrop-blur-sm shadow-xl" data-testid="sphere-card">
            <CardContent className="pt-10 pb-10 px-8">
              {/* Sphere Selection Header */}
              <div className="text-center mb-8">
                <p className="text-orange-500 font-manrope font-semibold text-sm uppercase tracking-wider mb-2">
                  {t('welcome.step_2')}
                </p>
                <h2 className="text-2xl font-manrope font-bold text-white mb-2">
                  {t('welcome.sphere_title')}
                </h2>
                <p className="text-gray-400 font-dm-sans text-sm">
                  {t('welcome.sphere_description')}
                </p>
              </div>

              {/* Sphere Options */}
              <div className="space-y-3 mb-8 max-h-[300px] overflow-y-auto pr-2">
                {spheres.map((sphere) => (
                  <button
                    key={sphere}
                    data-testid={`sphere-${sphere}`}
                    onClick={() => setSelectedSphere(sphere)}
                    className={`w-full p-4 rounded-lg border transition-all duration-200 flex items-center justify-between ${
                      selectedSphere === sphere
                        ? 'bg-orange-500 border-orange-500 text-white'
                        : 'bg-gray-900/50 border-gray-700 text-gray-300 hover:border-orange-500/50 hover:bg-gray-800'
                    }`}
                  >
                    <span className="font-dm-sans text-left">
                      {SPHERE_LABELS[sphere]?.[language] || sphere.replace(/_/g, ' ')}
                    </span>
                    {selectedSphere === sphere && (
                      <CheckCircle className="w-5 h-5" />
                    )}
                  </button>
                ))}
              </div>

              {/* Start Button */}
              <Button
                data-testid="start-test-btn"
                onClick={handleStart}
                disabled={!selectedSphere}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-6 rounded-md shadow-[0_0_15px_rgba(249,115,22,0.3)] transition-all hover:scale-[1.02] text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('welcome.start_button')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};
